<script setup>
// Assistente de IA — conversar com o assistente sem cliente do outro lado.
//
// É o mesmo cérebro do atendimento: o que aparece aqui é o que o cliente receberia. Por isso a
// tela mostra também o que NÃO vira mensagem — o motivo, os itens da base e o consumo.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import assistenteIaApi from 'dashboard/api/assistenteIa';
import { useAssistenteIaApi } from '../composables/useAssistenteIaApi';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const { t } = useI18n();
const route = useRoute();
const { erro, chamar } = useAssistenteIaApi();

// Reativo: trocar de assistente pela URL limpa a conversa e passa a falar com o novo (C8).
const assistenteId = computed(() => route.params.assistenteId);
const mensagens = ref([]);
const texto = ref('');
const enviando = ref(false);

// Um número por CONVERSA, não o id do assistente.
//
// Conferir só o id não bastava: "Limpar" com pedido em voo, e A → B → A com pedido em voo,
// devolvem o mesmo id e a resposta velha entrava numa conversa que já não era dela. Toda vez que
// a conversa é zerada — por "Limpar" ou por troca de assistente — este número muda, e a resposta
// que estiver voltando deixa de ter onde entrar (AIA-10, C2).
const conversaAtual = ref(0);

// O motivo com nome de gente. A tela mostrava o identificador cru — "fora_do_escopo" —, e os
// dois motivos novos da AIA-11 chegariam do mesmo jeito. Motivo que a tela não conhecer continua
// aparecendo como veio: melhor um identificador feio do que um campo vazio (AIA-11, U8).
const MOTIVOS = [
  'respondido',
  'conversa',
  'esclarecimento',
  'sem_informacao',
  'pedido_de_humano',
  'fora_do_escopo',
  'assunto_sensivel',
];

const nomeDoMotivo = motivo =>
  MOTIVOS.includes(motivo)
    ? t(`ASSISTENTE_IA.PLAYGROUND.MOTIVOS.${motivo.toUpperCase()}`)
    : motivo;

const enviar = async () => {
  const escrito = texto.value.trim();
  if (!escrito) return;

  const desteEnvio = conversaAtual.value;

  mensagens.value.push({ papel: 'cliente', texto: escrito });
  texto.value = '';
  enviando.value = true;

  // So vai o que e conversa. Entrada de erro nao e mensagem de ninguem e fica fora (AIA-13,
  // B4). A PASSAGEM vai, com papel proprio: ela nao e fala do assistente, mas e um LIMITE — o
  // que veio antes dela nao e continuacao de nada, e sem ela a rajada seguinte juntava as duas
  // perguntas e o pedido de pessoa casava de novo, para sempre (AIA-14, B1).
  const paraEnviar = mensagens.value
    .filter(
      m =>
        m.papel !== 'erro' && (m.papel === 'passagem' || m.texto.trim() !== '')
    )
    .map(m => ({ papel: m.papel, texto: m.texto }));

  const dados = await chamar(
    assistenteIaApi.playground(assistenteId.value, paraEnviar)
  );

  // A conversa mudou enquanto a resposta vinha: esta resposta (ou este erro) é de outra
  // conversa e não tem onde entrar. O spinner é desligado de qualquer jeito.
  if (desteEnvio !== conversaAtual.value) return;

  enviando.value = false;

  if (!dados) {
    mensagens.value.push({ papel: 'erro', texto: erro.value });
    return;
  }

  const resposta = dados.payload;
  mensagens.value.push({
    // Passagem e fala sao coisas diferentes, aqui como no atendimento: a mensagem de passagem
    // nao carrega motivo e nunca contou como fala do assistente (AIA-14, B1).
    papel: resposta.precisa_humano ? 'passagem' : 'assistente',
    texto: resposta.resposta,
    detalhes: resposta,
  });
};

const limpar = () => {
  // O número da conversa muda AQUI: é ele que invalida o pedido em voo.
  conversaAtual.value += 1;
  mensagens.value = [];
  erro.value = '';
  enviando.value = false;
};

watch(assistenteId, limpar);
</script>

<template>
  <section class="flex flex-col gap-4">
    <div class="flex items-center justify-between">
      <div>
        <h2 class="text-heading-2 text-n-slate-12">
          {{ t('ASSISTENTE_IA.PLAYGROUND.TITULO') }}
        </h2>
        <p class="mb-0 text-n-slate-11">{{ t('ASSISTENTE_IA.PLAYGROUND.AVISO') }}</p>
      </div>
      <Button
        :label="t('ASSISTENTE_IA.PLAYGROUND.LIMPAR')"
        variant="faded"
        color="slate"
        size="xs"
        @click="limpar"
      />
    </div>

    <div class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak min-h-64">
      <p v-if="!mensagens.length" class="mb-0 text-sm text-n-slate-11">
        {{ t('ASSISTENTE_IA.PLAYGROUND.VAZIO') }}
      </p>

      <div
        v-for="(mensagem, indice) in mensagens"
        :key="indice"
        class="flex flex-col gap-2"
        :class="mensagem.papel === 'cliente' ? 'items-end' : 'items-start'"
      >
        <div
          class="max-w-xl px-3 py-2 text-sm rounded-xl"
          :class="{
            'bg-n-brand text-white': mensagem.papel === 'cliente',
            'bg-n-slate-3 text-n-slate-12':
              mensagem.papel === 'assistente' || mensagem.papel === 'passagem',
            'bg-n-ruby-3 text-n-ruby-11': mensagem.papel === 'erro',
          }"
        >
          <span v-if="mensagem.texto">{{ mensagem.texto }}</span>
          <span v-else class="italic">—</span>
        </div>

        <div
          v-if="mensagem.detalhes"
          class="max-w-xl p-3 text-xs border rounded-lg border-n-weak text-n-slate-11 flex flex-col gap-1"
        >
          <span
            v-if="mensagem.detalhes.precisa_humano"
            class="px-2 py-0.5 rounded-full bg-n-amber-3 text-n-amber-11 w-fit"
          >
            {{ t('ASSISTENTE_IA.PLAYGROUND.PASSARIA') }}
          </span>
          <span>
            {{ t('ASSISTENTE_IA.PLAYGROUND.MOTIVO') }}:
            {{ nomeDoMotivo(mensagem.detalhes.motivo) }}
          </span>
          <span>
            {{ t('ASSISTENTE_IA.PLAYGROUND.TOKENS') }}:
            {{ mensagem.detalhes.tokens.entrada }} / {{ mensagem.detalhes.tokens.saida }} ·
            {{ t('ASSISTENTE_IA.PLAYGROUND.LATENCIA') }}:
            {{ mensagem.detalhes.latencia_ms }} ms
          </span>

          <details v-if="mensagem.detalhes.itens_usados.length">
            <summary class="cursor-pointer">
              {{ t('ASSISTENTE_IA.PLAYGROUND.ITENS') }}
            </summary>
            <div
              v-for="item in mensagem.detalhes.itens_usados"
              :key="`${item.tipo}-${item.id}`"
              class="mt-1"
            >
              <strong>{{ item.titulo || item.pergunta }}</strong>
              ({{ item.similaridade }})
              <div>{{ item.conteudo }}</div>
            </div>
          </details>
          <span v-else>{{ t('ASSISTENTE_IA.PLAYGROUND.NENHUM_ITEM') }}</span>
        </div>
      </div>
    </div>

    <div class="flex gap-2">
      <Input
        v-model="texto"
        class="flex-1"
        :placeholder="t('ASSISTENTE_IA.PLAYGROUND.ESCREVA')"
        @enter="enviar"
      />
      <Button
        :label="t('ASSISTENTE_IA.PLAYGROUND.ENVIAR')"
        :is-loading="enviando"
        size="sm"
        @click="enviar"
      />
    </div>
  </section>
</template>
